import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { ROUTES } from 'Constants';
import { EventDetailEntity } from 'Models/Entities';
import { whiteLabelStore } from 'Models/WhiteLabelStore';
import { Navigate } from 'react-router-dom';
import { ScrollToItem } from 'Validators/Functions/HumanWritten/ScrollToElement';
import { getFerryBookingTransactionIdFromStorage } from 'Services/Api/_HumanWritten/BookingService/BookingService';
import { fetchBookingsForCart } from 'Services/Api/_HumanWritten/BookingService/FerryTripBookingService';
import useAsync from 'Hooks/useAsync';
import { LottieSpinner } from 'Views/Components/_HumanWritten/Lottie/LottieSpinner';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { WizardStepHeader } from '../Shared/WizardStepHeader';
import { UserTypeTermsOverride } from './useUserTypeTermsOverride';

export type TermsAndConditionsType = 'vehicle' | 'passenger' | 'event' | 'freight';

export interface TermsAndConditionsProps {
	redirectOnAccepted: string;
	acceptedTsandCs: boolean;
	isMobileBookingWizard: boolean;
	termsAndConditionsTypes: TermsAndConditionsType[];
	isAlteration: boolean;
	bookingToEditId?: string;
	eventDetails?: EventDetailEntity;
	userTypeTerms?: UserTypeTermsOverride;
}

const pickTerms = (override: string | undefined, fallback: string | undefined) => {
	if (override && override.toLowerCase() !== 'none') {
		return override;
	}
	if (fallback && fallback.toLowerCase() !== 'none') {
		return fallback;
	}
	return '';
};

interface TermsSection {
	id: string;
	titleId: string;
	defaultTitle: string;
	iconName: string;
	content: string;
}

const TERMS_META: Record<
	TermsAndConditionsType | 'general',
	{ titleId: string; defaultTitle: string; iconName: string }
> = {
	vehicle: {
		titleId: 'booking-wizard__terms__section-title__vehicle',
		defaultTitle: 'Vehicle terms',
		iconName: 'directions_car',
	},
	passenger: {
		titleId: 'booking-wizard__terms__section-title__passenger',
		defaultTitle: 'Passenger terms',
		iconName: 'groups',
	},
	freight: {
		titleId: 'booking-wizard__terms__section-title__freight',
		defaultTitle: 'Freight terms',
		iconName: 'inventory_2',
	},
	event: { titleId: 'booking-wizard__terms__section-title__event', defaultTitle: 'Event terms', iconName: 'event' },
	general: {
		titleId: 'booking-wizard__terms__section-title__general',
		defaultTitle: 'General terms',
		iconName: 'gavel',
	},
};

export function TermsAndConditions({
	redirectOnAccepted,
	acceptedTsandCs,
	termsAndConditionsTypes,
	isAlteration,
	bookingToEditId,
	eventDetails,
	userTypeTerms,
}: TermsAndConditionsProps) {
	const [currentTerms, setCurrentTerms] = useState<TermsAndConditionsType[]>(termsAndConditionsTypes);
	useEffect(() => {
		ScrollToItem('terms-and-conditions-title', 'end');
	}, []);

	const transactionId = getFerryBookingTransactionIdFromStorage();

	// Depend on a primitive (boolean) rather than the array reference. The
	// parent sometimes passes `termsAndConditionsTypes ?? []` which mints a
	// fresh empty array every render — using the array as a useAsync dep would
	// re-fire the effect on every render, leaving the component stuck in the
	// loading branch and the header never gets to mount.
	const hasInitialTermsTypes = termsAndConditionsTypes.length > 0;
	const { type } = useAsync(async () => {
		if (hasInitialTermsTypes) return;
		const bookings = await fetchBookingsForCart(transactionId);
		const _terms: TermsAndConditionsType[] = [];
		bookings.forEach(booking => {
			if (!!booking.bookingSummaries.recentSummary.cargoInfo) {
				_terms.push('vehicle');
			} else if (!!booking.bookingSummaries.recentSummary.freightDetail) {
				_terms.push('freight');
			} else {
				_terms.push('passenger');
			}
		});
		setCurrentTerms(Array.from(new Set(_terms)));
	}, [transactionId, hasInitialTermsTypes]);

	if (acceptedTsandCs) {
		return <Navigate to={redirectOnAccepted} replace />;
	}

	const distinctTerms = Array.from(new Set(currentTerms));
	const sections: TermsSection[] = [];
	for (const term of distinctTerms) {
		let tnc = '';
		switch (term) {
			case 'vehicle':
				tnc = pickTerms(userTypeTerms?.vehicle, whiteLabelStore.vehicleTerms);
				break;
			case 'passenger':
				tnc = pickTerms(userTypeTerms?.passenger, whiteLabelStore.passengerTerms);
				break;
			case 'freight':
				tnc = pickTerms(userTypeTerms?.freight, whiteLabelStore.freightTerms);
				break;
			case 'event':
				tnc = eventDetails?.termsAndConditions ?? '';
				break;
			default:
				tnc = whiteLabelStore.passengerTerms;
		}
		if (tnc && tnc.trim() !== '') {
			sections.push({ id: term, ...TERMS_META[term], content: tnc });
		}
	}
	const generalTerms = whiteLabelStore.generalTerms;
	if (!distinctTerms.includes('event') && generalTerms && generalTerms.toLowerCase() !== 'none') {
		sections.push({ id: 'general', ...TERMS_META.general, content: generalTerms });
	}

	if (type === 'loading') {
		return <LottieSpinner />;
	}

	return (
		<div
			className={`terms-and-conditions-container ${distinctTerms.includes('event') ? 'event-terms-and-conditions' : ''}`}
		>
			<WizardStepHeader
				iconName="gavel"
				title={<FormattedMessage id="booking-wizard__terms__header" defaultMessage="Terms & Conditions" />}
				subtitle={
					<FormattedMessage
						id="booking-wizard__terms__subtitle"
						defaultMessage="Please review and accept before continuing."
					/>
				}
				showCloseLink={isAlteration}
				bookingToEditId={bookingToEditId}
				className="terms-and-conditions-title"
			/>
			<div className="terms-and-conditions__content booking-section-stack">
				{sections.map(section => (
					<section key={section.id} className="terms-and-conditions__section booking-section-card">
						<header className="booking-section-header">
							<span className="booking-section-heading__icon">
								<Icon name={section.iconName} useMaterialIcons />
							</span>
							<div className="booking-section-header__text">
								<h4 className="booking-section-title">
									<FormattedMessage id={section.titleId} defaultMessage={section.defaultTitle} />
								</h4>
							</div>
						</header>
						{/* eslint-disable react/no-danger */}
						{/* nosemgrep: eslint.react-dangerouslysetinnerhtml */}
						<div className="wysiwyg-content" dangerouslySetInnerHTML={{ __html: section.content }} />
						{/* eslint-enable react/no-danger */}
					</section>
				))}
			</div>
		</div>
	);
}
